// shared/Button.jsx
import React, { forwardRef, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { motion } from "framer-motion";
import Spinner from "../../shared/spinner/Spinner";
import { mergeRefs } from "../utils/mergeRefs";
import { twMerge } from "tailwind-merge";

const BASE =
  "flex items-center justify-center gap-2 transition-colors duration-300 font-semibold";
const ANIMATED = "transition-transform duration-150 active:scale-95";

const MAPS = {
  color: {
    black:
      "bg-black text-white hover:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),_0px_0px_0px_4px_#F2F4F7] disabled:bg-gray50 disabled:text-gray300",
    gray100:
      "bg-gray100 text-gray700 hover:bg-gray200 hover:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),_0px_0px_0px_4px_#F2F4F7] active:bg-gray200 active:text-gray800 disabled:bg-gray50 disabled:text-gray300",
    primary50:
      "bg-primary50 text-primary700 hover:bg-primary100 hover:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF] active:bg-primary200 active:text-primary800 disabled:bg-primary50 disabled:text-primary100",
    primary500:
      "bg-primary500 text-white hover:bg-primary600 hover:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF] active:bg-primary700 disabled:bg-primary200",
    error600:
      "bg-error600 text-white hover:bg-error700 hover:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),_0_0_0_4px_#FEE4E2] active:bg-error800 disabled:bg-error200",
    "white/gray300":
      "border border-gray300 bg-white text-gray700 hover:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),_0px_0px_0px_4px_#F2F4F7] disabled:bg-gray50 disabled:text-gray300",
    "white/primary700":
      "border border-primary700 bg-white text-primary700 hover:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),_0_0_0_4px_#F4EBFF] active:bg-primary50 active:text-primary600 disabled:border-primary100 disabled:text-primary100",
    "transparent/primary700":
      "border border-primary700 bg-transparent text-primary700 hover:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),_0_0_0_4px_#F4EBFF] active:bg-primary50 active:text-primary600 disabled:border-primary100 disabled:text-primary100",
    "white/error300":
      "border border-error300 bg-white text-error700 hover:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),_0_0_0_4px_#FEE4E2] active:bg-error50 active:text-error600 disabled:border-error100 disabled:text-error100",
    "white/hover:shadow":
      "bg-white text-gray700 hover:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),_0px_0px_0px_4px_#F2F4F7] disabled:bg-gray50 disabled:text-gray300",
    grayTrue700:
      "bg-grayTrue700 hover:bg-grayTrue800 hover:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),_0_0_0_4px_#F4EBFF] active:bg-grayTrue800 active:text-primary300 disabled:opacity-50 disabled:text-gray300",
    "transparent/grayTrue700":
      "bg-transparent text-white hover:bg-grayTrue700 active:bg-grayTrue700 active:text-primary300 disabled:opacity-50 disabled:text-gray300",
  },
  rounded: {
    full: "rounded-full",
    lg: "rounded-lg",
    xl: "rounded-xl",
    md: "rounded-md",
    sm: "rounded",
  },
  size: {
    "2xl": "min-h-15 h-15",
    lg: "h-11 min-h-11",
    md: "h-[2.625rem] min-h-[2.625rem]",
    sm: "h-10 min-h-10 text-sm",
    xl: "h-12 min-h-12",
    xs: "h-8 min-h-8",
    xxs: "h-6 min-h-6",
  },
};

const Button = forwardRef(function Button(props, ref) {
  const {
    "aria-selected": ariaSelected,
    autoFocus = false,
    children,
    className,
    color = "primary500",
    endContent,
    id,
    isAnimated = true,
    isDisabled = false,
    isIconOnly = false,
    isLoading = false,
    onClick,
    rounded = "full",
    size = "md",
    startContent,
    type = "button",
    ...rest
  } = props;

  const innerRef = useRef(null);

  useEffect(() => {
    if (autoFocus && innerRef.current) innerRef.current.focus();
  }, [autoFocus]);

  const clickable = !isDisabled && !isLoading;

  // Compound variant logic for icon-only buttons
  const getIconOnlyClasses = () => {
    if (!isIconOnly) return null;

    const iconSizeMap = {
      xxs: "w-6 min-w-6",
      xs: "w-8 min-w-8",
      sm: "w-9 min-w-9",
      md: "w-[2.625rem] min-w-[2.625rem]",
      lg: "w-11 min-w-11",
      xl: "w-12 min-w-12",
      "2xl": "w-15 min-w-15",
    };

    return iconSizeMap[size] || iconSizeMap.md;
  };

  const classes = twMerge(
    BASE,
    isAnimated && ANIMATED,
    MAPS.color[color],
    MAPS.rounded[rounded],
    MAPS.size[size],
    isIconOnly ? "rounded-full p-0" : "px-4", // variant rule
    getIconOnlyClasses(), // compound size override
    (isDisabled || isLoading) && "cursor-not-allowed",
    isLoading && "opacity-40",
    className
  );

  const handleClick = (e) => {
    e.stopPropagation();
    if (!clickable) return;
    onClick && onClick(e);
  };

  return (
    <motion.button
      id={id}
      aria-selected={ariaSelected}
      className={classes}
      disabled={!clickable}
      onClick={handleClick}
      ref={mergeRefs(innerRef, ref)}
      type={type}
      {...rest}
    >
      {isLoading && <Spinner color="white" size="sm" />}
      {!isLoading && startContent ? startContent : null}
      {isLoading && isIconOnly ? null : children}
      {!isLoading && endContent ? endContent : null}
    </motion.button>
  );
});

Button.propTypes = {
  "aria-selected": PropTypes.bool,
  autoFocus: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.oneOf([
    "black",
    "gray100",
    "primary50",
    "primary500",
    "error600",
    "white/gray300",
    "white/primary700",
    "transparent/primary700",
    "white/error300",
    "white/hover:shadow",
    "grayTrue700",
    "grayTrue800",
  ]),
  endContent: PropTypes.node,
  id: PropTypes.string,
  isAnimated: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isIconOnly: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func,
  rounded: PropTypes.oneOf(["full", "lg", "xl", "md", "sm"]),
  size: PropTypes.oneOf(["2xl", "lg", "md", "sm", "xl", "xs", "xxs"]),
  startContent: PropTypes.node,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
};

export default Button;
